import { useMemo, useState, useCallback, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import _ from "lodash"

import { ImdfFeature } from "providers/venue/types"
import {
  DEFAULT_FILTER_VALUE,
  DINING_PAGE_CONFIG,
  DINING_TAXONOMY_TAG,
  GTM_VIEW_DINING_CATEGORY,
  Q_VENUE,
} from "constant"

import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { useVenueController } from "hooks/venue/useVenueController"
import { useGetOccupants } from "providers/venue/modules/data/occupants/useGetOccupants"
import { useTranslation } from "providers/i18n"

import { pushDataLayer } from "utils/googletagmanager"
import { useInfiniteGetOccupants } from "providers/venue/modules/data/occupants/useInfiniteGetOccupants"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { createSortingConfigFeatureByName } from "utils/feature"
import dayjs from "dayjs"
import { useRecommendedDirectory } from "hooks/sponsored-content/recommended"
import { useGetPageConfig } from "providers/venue/modules/data"

type FilterByVenueId = (venueId: string) => void
type FilterByCategoryId = (id: string) => void
type FilterByOrdinal = (ordinal: number) => void
type OrdinalFilterValue = number | "ALL"

const DINING_QUERY_KEY = ["dining"]

export const useDining = () => {
  const {
    i18n: { language: chosenLanguage },
  } = useTranslation()
  const {
    isMultiVenueProject,
    levels,
    venues,
    occupants,
    findFeatureById,
    dataLoaded,
    pageConfig,
    taxonomies,
  } = useVenue()
  const [category, setCategory] = useState(DEFAULT_FILTER_VALUE)
  const [ordinal, setOrdinal] =
    useState<OrdinalFilterValue>(DEFAULT_FILTER_VALUE)
  const { locationVenue } = useGeoLocation()
  const locationVenueId = _.get(locationVenue, "id")
  const { viewingVenue: venueFilter } = useVenueController(locationVenueId)
  const [, setSearchParams] = useSearchParams()
  const { metadata } = useGetPageConfig(DINING_PAGE_CONFIG)
  const { queryBy = "default" } = metadata || {}

  const { recommendedDirectories, recommendedDirectoryConfig } =
    useRecommendedDirectory()

  const filterByVenueId: FilterByVenueId = useCallback(
    (venueId) => {
      if (venueId) setSearchParams({ [Q_VENUE]: venueId }, { replace: true })
    },
    [setSearchParams]
  )

  const diningTaxonomyIds = useMemo(() => {
    return (
      taxonomies
        ?.filter((taxonomy) =>
          taxonomy.properties?.tags?.includes(DINING_TAXONOMY_TAG)
        )
        ?.map((taxonomy) => taxonomy.id) ?? []
    )
  }, [taxonomies])

  const categoryFilters = {
    taxonomy_tag: {
      local_category_ids: diningTaxonomyIds,
    },
    default: {
      category: "restaurant",
    },
  }

  /**
   * Result All Filtered Operating Restaurants on viewing venue
   */
  const { data: viewingVenueOperatingRestaurants } = useGetOccupants({
    queryKey: DINING_QUERY_KEY,
    pagination: {
      perPage: _.size(occupants),
    },
    sort: [
      {
        field: "properties.is_featured",
        order: "desc",
      },
      {
        field: "properties.featured_image",
        order: "asc",
      },
      {
        field: "properties.logo",
        order: "asc",
      },
      ...createSortingConfigFeatureByName(chosenLanguage),
    ],
    filter: {
      properties: {
        anchor: {
          //@ts-ignore
          $exists: true,
        },
        //@ts-ignore
        venue: {
          id: venueFilter,
        },
        ...categoryFilters[queryBy],
      },
    },
    enabled: dataLoaded,
  })

  /**
   * Result All Filtered Restaurants (without ordinal filter)
   * - Used to create an array of unique levels of restaurants that match the category
   * - Used as the source data for the useInfiniteGetOccupants hook
   */
  const { data: allFilteredRestaurantsSorted } = useGetOccupants({
    queryKey: DINING_QUERY_KEY,
    pagination: {
      perPage: _.size(viewingVenueOperatingRestaurants),
    },
    sort: [
      {
        field: "properties.is_featured",
        order: "desc",
      },
      {
        field: "properties.featured_image",
        order: "asc",
      },
      {
        field: "properties.logo",
        order: "asc",
      },
      ...createSortingConfigFeatureByName(chosenLanguage),
    ],
    filter: {
      properties: {
        local_categories: {
          $elemMatch: {
            id: category,
          },
        },
        anchor: {
          //@ts-ignore
          $exists: true,
        },
        //@ts-ignore
        venue: {
          id: venueFilter,
        },
        ...categoryFilters[queryBy],
      },
    },
    enabled: !!viewingVenueOperatingRestaurants,
  })

  /**
   * Result Infinite Filtered Restaurants
   * - Used to display data in an infinite scroll version
   */
  const {
    data: filteredRestaurantsSorted,
    hasNextPage,
    isFetched,
    fetchNextPage,
  } = useInfiniteGetOccupants({
    queryKey: DINING_QUERY_KEY,
    pagination: {
      perPage: 25,
    },
    sort: [
      {
        field: "properties.is_featured",
        order: "desc",
      },
      {
        field: "properties.featured_image",
        order: "asc",
      },
      {
        field: "properties.logo",
        order: "asc",
      },
      ...createSortingConfigFeatureByName(chosenLanguage),
    ],
    filter: {
      properties: {
        local_categories: {
          $elemMatch: {
            id: category,
          },
        },
        anchor: {
          //@ts-ignore
          $exists: true,
        },
        //@ts-ignore
        venue: {
          id: venueFilter,
        },
        //@ts-ignore
        ordinal,
        ...categoryFilters[queryBy],
      },
    },
    enabled: !!allFilteredRestaurantsSorted,
  })

  const restaurantSubCategories = useMemo(
    () =>
      _(viewingVenueOperatingRestaurants)
        //@ts-ignore
        .map((restaurant) => restaurant.properties.local_categories)
        .compact()
        .flatten()
        .uniqBy((category) => category.id)
        .filter((category) => category.properties.parent_id !== null)
        .sortBy("properties.order")
        .value(),
    [viewingVenueOperatingRestaurants]
  )

  const filteredLevels = useMemo(
    () =>
      _(allFilteredRestaurantsSorted)
        .uniqBy("properties.ordinal")
        .filter((item) => !_.isNil(item.properties.ordinal))
        .map(({ properties: { ordinal } }) =>
          levels.find(
            (level: ImdfFeature) => level.properties.ordinal === ordinal
          )
        )
        .sortBy("properties.ordinal")
        .value(),
    [levels, allFilteredRestaurantsSorted]
  )

  //check isNewShop and isComingSoon
  const processedRestaurantData = useMemo(() => {
    if (_.isEmpty(filteredRestaurantsSorted?.pages))
      return filteredRestaurantsSorted

    const currentDate = dayjs()
    const periodInDays = _.get(pageConfig, "newShop.metadata.periodInDays", 30)

    return {
      pages: filteredRestaurantsSorted.pages.map((page) => ({
        ...page,
        data: page.data.map((occupant) => {
          if (!occupant?.properties?.start_date) {
            return occupant
          }

          const startDate = dayjs(occupant.properties.start_date)
          const isNewShop =
            currentDate.isSameOrAfter(startDate) &&
            currentDate.diff(startDate, "day") <= periodInDays

          const isComingSoon = startDate.isAfter(currentDate)

          return {
            ...occupant,
            properties: {
              ...occupant.properties,
              isNewShop,
              isComingSoon,
            },
          }
        }),
      })),
      pageParams: filteredRestaurantsSorted.pageParams,
    }
  }, [pageConfig, filteredRestaurantsSorted])

  // every xxx record has 1 sponsored-content
  const sponsoredContentFrequency = useMemo(
    () => _.get(recommendedDirectoryConfig, "frequency", 2),
    [recommendedDirectoryConfig]
  )

  const recommendedRestaurants = useMemo(
    () =>
      recommendedDirectories?.filter(
        (occupant) => occupant.properties.category === "restaurant"
      ),
    [recommendedDirectories]
  )

  const attachedAdsResult = useMemo(() => {
    if (
      _.isEmpty(processedRestaurantData?.pages) ||
      _.isEmpty(recommendedRestaurants)
    )
      return processedRestaurantData

    // frequency <= 0 means "pin all sponsored content to the top"
    if (sponsoredContentFrequency <= 0) {
      return {
        pages: processedRestaurantData.pages.map((page, pageIndex) => ({
          ...page,
          data:
            pageIndex === 0
              ? [...recommendedRestaurants, ...(page.data || [])]
              : page.data,
        })),
        pageParams: processedRestaurantData.pageParams,
      }
    }

    let adIndex = 0
    const insertAdsIntoResult = (data) => {
      return data.flatMap((chunk) =>
        adIndex < recommendedRestaurants.length
          ? chunk.concat(recommendedRestaurants[adIndex++])
          : chunk
      )
    }
    return {
      pages: processedRestaurantData.pages.map((page) => ({
        ...page,
        data: insertAdsIntoResult(
          _.chunk(page.data || [], sponsoredContentFrequency)
        ),
      })),
      pageParams: processedRestaurantData.pageParams,
    }
  }, [
    processedRestaurantData,
    recommendedRestaurants,
    sponsoredContentFrequency,
  ])

  const hasData = _.size(filteredRestaurantsSorted?.pages[0]?.data) > 0

  const filterByCategoryId: FilterByCategoryId = useCallback((id: string) => {
    if (id === null) return
    setCategory(id)
  }, [])

  const filterByOrdinal: FilterByOrdinal = useCallback(
    (ordinal: OrdinalFilterValue) => {
      if (ordinal === null) return
      setOrdinal(ordinal)
    },
    []
  )

  const onReachEndScroll = useCallback(
    (inView) => {
      if (inView && hasNextPage) fetchNextPage()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasNextPage]
  )

  const { endScrollRef } = useInfiniteScroll({
    onReachEndScroll,
  })

  useEffect(() => {
    if (category && category !== DEFAULT_FILTER_VALUE) {
      pushDataLayer({
        event: GTM_VIEW_DINING_CATEGORY,
        dining_category_id: category,
        dining_category_name: _.get(
          findFeatureById(category),
          "properties.name.en"
        ),
      })
    }
  }, [findFeatureById, category])

  return {
    restaurants: attachedAdsResult,
    categories: restaurantSubCategories,
    currentCategory: category,
    levels: filteredLevels,
    isMultiVenueProject,
    locationVenueId,
    venues,
    venueFilter,
    hasNextPage,
    hasData,
    isFetched,
    endScrollRef,
    filterByCategoryId,
    filterByOrdinal,
    filterByVenueId,
  }
}
